import React, { useState, useRef } from 'react';
import { 
  ArrowLeft, Calendar, Users, Code, Layout, FileText, Search, 
  Smartphone, Globe, Settings, CheckCircle, Zap, Target, 
  Database, Shield, Palette, Cpu, Clock, TrendingUp,
  Play, Pause, Monitor, Maximize2, Video, ChevronRight,
  Award, Eye, MessageCircle, Mail, Phone, Wifi, Languages,
  Battery, Droplets, Zap as Lightning, Cloud, Building,
  BarChart, ShieldCheck, Server, Cpu as CpuIcon,
  Globe as GlobeIcon, Package, Filter, MessageSquare,
  Download, MapPin, ExternalLink, Crown, Users as UsersIcon
} from 'lucide-react';

export default function AzureProject({ darkMode, language, onBack }) {
  const [activeSection, setActiveSection] = useState('overview');
  const [playingVideo, setPlayingVideo] = useState(null);
  
  const videoRef = useRef(null);
  
  const isRTL = language === 'ar';

  const handleVideoPlay = () => {
    if (videoRef.current) {
      if (playingVideo) {
        videoRef.current.pause();
        setPlayingVideo(null);
      } else {
        videoRef.current.play();
        setPlayingVideo(true);
      }
    }
  };

  const handleVideoPause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setPlayingVideo(false);
    }
  };

  const content = {
    fr: {
      title: 'Azure Energy & Water',
      subtitle: 'Site corporate multilingue pour solutions énergétiques et hydriques',
      backButton: 'Retour aux projets',
      
      // Navigation
      navOverview: 'Vue d\'ensemble',
      navDemo: 'Démonstration',
      navFeatures: 'Fonctionnalités',
      navTech: 'Technologies',
      navSeo: 'SEO & Performance',
      navResults: 'Résultats',
      
      // Hero Stats
      stats: [
        { value: '3 Langues', label: 'Multilingue', icon: Languages },
        { value: '8 Domaines', label: 'Expertises', icon: Building },
        { value: '99%', label: 'Disponibilité', icon: ShieldCheck },
        { value: '<1.5s', label: 'Performance', icon: Zap }
      ],
      
      // Overview
      overviewTitle: 'Projet Azure Energy & Water',
      overviewIntro: 'Développement d\'un site corporate complet pour Azure Energy & Water, leader marocain des solutions énergétiques et hydriques. Plateforme trilingue présentant 8 domaines d\'expertise et un catalogue de produits techniques.',
      
      projectGoals: 'Objectifs du Projet',
      goalsList: [
        'Créer une vitrine professionnelle reflétant l\'expertise technique du groupe',
        'Implémenter un système multilingue complet (FR/EN/ES)',
        'Présenter clairement les 8 domaines d\'expertise et solutions produits',
        'Optimiser la génération de leads via des formulaires spécialisés',
        'Assurer une performance optimale malgré le contenu riche'
      ],
      
      myRole: 'Mon Rôle - Développeur Full Stack Solo',
      roleDetails: [
        'Conception de l\'architecture technique et de la base de données',
        'Développement du système multilingue avancé',
        'Intégration du catalogue produits et des solutions techniques',
        'Création des formulaires de demande d\'étude personnalisés',
        'Optimisation SEO technique et sémantique trilingue',
        'Déploiement et configuration de l\'environnement de production'
      ],
      
      // Demonstration Section
      demoTitle: 'Démonstration Complète',
      demoSubtitle: 'Découvrez le site en action : navigation, multilinguisme et fonctionnalités',
      
      videoTitle: 'Tour d\'horizon du site',
      videoDescription: 'Cette démo montre les fonctionnalités clés : basculement de langue, exploration des expertises, consultation des produits et soumission de demande.',
      
      keyFeatures: 'Points clés démontrés',
      keyFeaturesList: [
        'Navigation fluide entre français, anglais et espagnol',
        'Exploration des 8 domaines d\'expertise techniques',
        'Consultation détaillée des solutions et produits',
        'Processus complet de demande d\'étude gratuite',
        'Design responsive adapté à tous les appareils'
      ],
      
      // Features Section
      featuresTitle: 'Fonctionnalités Avancées',
      featuresSubtitle: 'Système complet pour la présentation d\'expertise technique',
      
      featuresList: [
        {
          icon: Languages,
          title: 'Système Multilingue Complet',
          description: 'Interface entièrement disponible en français, anglais et espagnol avec gestion centralisée des traductions',
          details: [
            'Traduction de l\'interface et du contenu technique',
            'URLs optimisées par langue (/fr, /en, /es)',
            'Sélecteur de langue persistant',
            'Gestion des formats de date et nombres',
            'SEO multilingue optimisé'
          ]
        },
        {
          icon: Building,
          title: 'Présentation des 8 Expertises',
          description: 'Organisation claire des domaines d\'activité : Énergies Renouvelables, Traitement de l\'Eau, Décarbonation, etc.',
          details: [
            'Architecture modulaire pour chaque domaine',
            'Contenu technique détaillé et illustré',
            'Navigation intuitive entre les expertises',
            'Call-to-action contextuels',
            'Système de recommandations croisées'
          ]
        },
        {
          icon: Package,
          title: 'Catalogue de Solutions Produits',
          description: 'Présentation structurée des gammes de produits techniques avec fiches détaillées',
          details: [
            'Filtrage par catégorie et spécifications',
            'Fiches produits techniques complètes',
            'Galerie d\'images haute résolution',
            'Documentation technique téléchargeable',
            'Système de demande d\'information'
          ]
        },
        {
          icon: MessageSquare,
          title: 'Générateur de Leads Avancé',
          description: 'Formulaire intelligent de demande d\'étude gratuite avec qualification automatique',
          details: [
            'Champs conditionnels et adaptatifs',
            'Validation en temps réel',
            'Envoi d\'emails de confirmation',
            'Intégration avec outils CRM',
            'Protection anti-spam renforcée'
          ]
        },
        {
          icon: Globe,
          title: 'Performance Internationale',
          description: 'Optimisations techniques pour une audience mondiale',
          details: [
            'CDN pour distribution globale',
            'Cache avancé par langue',
            'Compression des ressources',
            'Images optimisées responsive',
            'Support des timezones multiples'
          ]
        },
        {
          icon: Shield,
          title: 'Sécurité Entreprise',
          description: 'Normes de sécurité élevées pour un site corporate',
          details: [
            'HTTPS avec certificat SSL étendu',
            'Protection contre les injections',
            'Sauvegardes automatiques quotidiennes',
            'Monitoring de sécurité 24/7',
            'Conformité RGPD multilingue'
          ]
        }
      ],
      
      // Technologies
      techTitle: 'Architecture Technique',
      techSubtitle: 'Stack moderne et robuste pour un projet international',
      
      frontendTech: {
        title: 'Frontend',
        technologies: [
          { name: 'Laravel Blade', purpose: 'Templates dynamiques multilingues' },
          { name: 'Tailwind CSS', purpose: 'Design system responsive' },
          { name: 'Alpine.js', purpose: 'Interactivité légère' },
          { name: 'Swiper.js', purpose: 'Carrousels et galeries' }
        ]
      },
      
      backendTech: {
        title: 'Backend',
        technologies: [
          { name: 'Laravel 10', purpose: 'Framework PHP avec gestion multilingue' },
          { name: 'MySQL', purpose: 'Base de données relationnelle' },
          { name: 'Redis', purpose: 'Cache sessions et données' },
          { name: 'Eloquent ORM', purpose: 'Modélisation des données' }
        ]
      },
      
      devOpsTech: {
        title: 'DevOps & Services',
        technologies: [
          { name: 'Git + GitHub', purpose: 'Versioning et collaboration' },
          { name: 'Composer/NPM', purpose: 'Gestion des dépendances' },
          { name: 'Cloudflare', purpose: 'CDN et sécurité' },
          { name: 'Backups automatisés', purpose: 'Sauvegarde quotidienne' }
        ]
      },
      
      // SEO Section
      seoTitle: 'Stratégie SEO Internationale',
      seoSubtitle: 'Optimisation pour 3 marchés linguistiques',
      
      seoStrategy: 'Approche SEO Multilingue',
      strategyPoints: [
        {
          title: 'Structure Internationale',
          items: [
            'Balises hreflang pour chaque langue',
            'Sitemaps XML séparés par langue',
            'URLs canoniques correctes',
            'Meta descriptions localisées',
            'Contenu unique par marché'
          ]
        },
        {
          title: 'Performance Technique',
          items: [
            'Score Core Web Vitals > 90',
            'Temps de chargement < 1.5s',
            'Images WebP optimisées',
            'Minification CSS/JS',
            'Cache HTTP efficace'
          ]
        },
        {
          title: 'Contenu Technique',
          items: [
            'Vocabulaire spécialisé localisé',
            'Structure sémantique HTML5',
            'Rich snippets produits/services',
            'Blog d\'expertise technique',
            'Liens internes optimisés'
          ]
        }
      ],
      
      performanceMetrics: 'Indicateurs Clés',
      metrics: [
        { metric: 'Largest Contentful Paint', value: '1.2s', target: '<2.5s', status: 'excellent' },
        { metric: 'First Input Delay', value: '35ms', target: '<100ms', status: 'excellent' },
        { metric: 'Cumulative Layout Shift', value: '0.08', target: '<0.1', status: 'excellent' },
        { metric: 'Time to Interactive', value: '1.8s', target: '<3.8s', status: 'excellent' }
      ],
      
      // Results
      resultsTitle: 'Impact & Résultats',
      resultsSubtitle: 'Performance commerciale et technique mesurable',
      
      achievements: 'Réalisations Clés',
      achievementsList: [
        {
          metric: 'Couverture Internationale',
          description: 'Site accessible en 3 langues pour 3 marchés',
          value: '+300%',
          icon: Globe
        },
        {
          metric: 'Expertise Présentée',
          description: '8 domaines techniques documentés en détail',
          value: '100%',
          icon: Building
        },
        {
          metric: 'Leads Générés',
          description: 'Demandes d\'étude qualifiées mensuellement',
          value: '+85%',
          icon: MessageSquare
        },
        {
          metric: 'Performance Site',
          description: 'Score Google PageSpeed sur desktop',
          value: '96/100',
          icon: Zap
        }
      ],
      
      clientTestimonial: {
        quote: 'Le site développé a parfaitement capté notre expertise technique complexe tout en restant accessible à notre audience internationale. Le système multilingue fonctionne impeccablement et a déjà généré des opportunités commerciales concrètes.',
        author: 'Direction Technique',
        position: 'Azure Energy & Water'
      },
      
      keySuccess: 'Facteurs de Succès',
      successPoints: [
        'Compréhension approfondie des enjeux techniques du secteur',
        'Architecture scalable pour contenu multilingue complexe',
        'Interface équilibrant profondeur technique et simplicité',
        'Optimisations performances pour audience internationale',
        'Support continu et améliorations itératives'
      ],
      
      projectInfo: {
        status: 'En ligne & Maintenu',
        team: 'Développeur solo',
        stack: 'Laravel, MySQL, Tailwind'
      },
      
      // Video section
      playVideo: 'Lancer la démonstration',
      pauseVideo: 'Mettre en pause',
      videoPlaying: 'En cours de lecture...'
    },
    ar: {
      title: 'Azure Energy & Water',
      subtitle: 'موقع شركة متعدد اللغات لحلول الطاقة والمياه',
      backButton: 'العودة للمشاريع',
      
      // Navigation
      navOverview: 'نظرة عامة',
      navDemo: 'التجربة',
      navFeatures: 'المميزات',
      navTech: 'التقنيات',
      navSeo: 'SEO والأداء',
      navResults: 'النتائج',
      
      // Hero Stats
      stats: [
        { value: '3 لغات', label: 'متعدد اللغات', icon: Languages },
        { value: '8 مجالات', label: 'التخصصات', icon: Building },
        { value: '99%', label: 'التوفر', icon: ShieldCheck },
        { value: '<1.5ث', label: 'الأداء', icon: Zap }
      ],
      
      // Overview
      overviewTitle: 'مشروع Azure Energy & Water',
      overviewIntro: 'تطوير موقع شركة كامل لـ Azure Energy & Water، الرائد المغربي في حلول الطاقة والمياه. منصة ثلاثية اللغات تقدم 8 مجالات تخصص وكتالوج المنتجات التقنية.',
      
      projectGoals: 'أهداف المشروع',
      goalsList: [
        'إنشاء واجهة احترافية تعكس الخبرة التقنية للمجموعة',
        'تنفيذ نظام متعدد اللغات كامل (فرنسي/إنجليزي/إسباني)',
        'عرض واضح لمجالات التخصص الثمانية وحلول المنتجات',
        'تحسين توليد العملاء المحتملين عبر نماذج متخصصة',
        'ضمان أداء مثالي رغم المحتوى الغني'
      ],
      
      myRole: 'دوري - مطور Full Stack منفرد',
      roleDetails: [
        'تصميم البنية التقنية وقاعدة البيانات',
        'تطوير نظام متعدد اللغات متقدم',
        'دمج كتالوج المنتجات والحلول التقنية',
        'إنشاء نماذج طلب دراسة مخصصة',
        'تحسين SEO التقني والدلالي ثلاثي اللغات',
        'النشر وتهيئة بيئة الإنتاج'
      ],
      
      // Demonstration Section
      demoTitle: 'تجربة كاملة',
      demoSubtitle: 'اكتشف الموقع أثناء العمل: التنقل، تعدد اللغات والميزات',
      
      videoTitle: 'جولة في الموقع',
      videoDescription: 'هذه التجربة تظهر الميزات الرئيسية: تبديل اللغة، استكشاف التخصصات، استشارة المنتجات وتقديم الطلبات.',
      
      keyFeatures: 'النقاط الرئيسية المقدمة',
      keyFeaturesList: [
        'تنقل سلس بين الفرنسية، الإنجليزية والإسبانية',
        'استكشاف مجالات التخصص التقنية الثمانية',
        'استشارة مفصلة للحلول والمنتجات',
        'عملية كاملة لطلب دراسة مجانية',
        'تصميم متجاوب متكيف مع جميع الأجهزة'
      ],
      
      // Features Section
      featuresTitle: 'ميزات متقدمة',
      featuresSubtitle: 'نظام كامل لعرض الخبرة التقنية',
      
      featuresList: [
        {
          icon: Languages,
          title: 'نظام متعدد اللغات كامل',
          description: 'واجهة متوفرة بالكامل بالفرنسية، الإنجليزية والإسبانية مع إدارة مركزية للترجمات',
          details: [
            'ترجمة الواجهة والمحتوى التقني',
            'عناوين URL محسنة حسب اللغة',
            'محدد لغة ثابت',
            'إدارة تنسيقات التاريخ والأرقام',
            'SEO متعدد اللغات محسن'
          ]
        },
        {
          icon: Building,
          title: 'عرض التخصصات الثمانية',
          description: 'تنظيم واضح لمجالات النشاط: الطاقات المتجددة، معالجة المياه، إزالة الكربون، إلخ.',
          details: [
            'بنية نمطية لكل مجال',
            'محتوى تقني مفصل وموضح',
            'تنقل بديهي بين التخصصات',
            'دعوات للإجراء سياقية',
            'نظام توصيات متقاطعة'
          ]
        },
        {
          icon: Package,
          title: 'كتالوج حلول المنتجات',
          description: 'عرض منظم لمجموعات المنتجات التقنية مع بطاقات تفصيلية',
          details: [
            'تصفية حسب الفئة والمواصفات',
            'بطاقات منتجات تقنية كاملة',
            'معرض صور عالية الدقة',
            'وثائق تقنية قابلة للتحميل',
            'نظام طلب المعلومات'
          ]
        },
        {
          icon: MessageSquare,
          title: 'مولد عملاء محتملين متقدم',
          description: 'نموذج ذكي لطلب دراسة مجانية مع تأهيل تلقائي',
          details: [
            'حقول شرطية وتكيفية',
            'تحقق في الوقت الفعلي',
            'إرسال إيميلات تأكيد',
            'تكامل مع أدوات CRM',
            'حماية مضادة للبريد العشوائي معززة'
          ]
        },
        {
          icon: Globe,
          title: 'أداء دولي',
          description: 'تحسينات تقنية لجمهور عالمي',
          details: [
            'CDN للتوزيع العالمي',
            'كاش متقدم حسب اللغة',
            'ضغط الموارد',
            'صور محسنة متجاوبة',
            'دعم مناطق زمنية متعددة'
          ]
        },
        {
          icon: Shield,
          title: 'أمان المؤسسة',
          description: 'معايير أمان عالية لموقع شركة',
          details: [
            'HTTPS مع شهادة SSL موسعة',
            'حماية ضد الحقن',
            'نسخ احتياطي تلقائي يومي',
            'مراقبة الأمان 24/7',
            'توافق RGPD متعدد اللغات'
          ]
        }
      ],
      
      // Technologies
      techTitle: 'البنية التقنية',
      techSubtitle: 'مجموعة حديثة وقوية لمشروع دولي',
      
      frontendTech: {
        title: 'الواجهة الأمامية',
        technologies: [
          { name: 'Laravel Blade', purpose: 'قوالب ديناميكية متعددة اللغات' },
          { name: 'Tailwind CSS', purpose: 'نظام تصميم متجاوب' },
          { name: 'Alpine.js', purpose: 'تفاعلية خفيفة' },
          { name: 'Swiper.js', purpose: 'العروض الشرائحية والمعارض' }
        ]
      },
      
      backendTech: {
        title: 'الخادم الخلفي',
        technologies: [
          { name: 'Laravel 10', purpose: 'إطار عمل PHP مع إدارة متعددة اللغات' },
          { name: 'MySQL', purpose: 'قاعدة بيانات علائقية' },
          { name: 'Redis', purpose: 'كاش الجلسات والبيانات' },
          { name: 'Eloquent ORM', purpose: 'نمذجة البيانات' }
        ]
      },
      
      devOpsTech: {
        title: 'DevOps والخدمات',
        technologies: [
          { name: 'Git + GitHub', purpose: 'التحكم بالإصدار والتعاون' },
          { name: 'Composer/NPM', purpose: 'إدارة التبعيات' },
          { name: 'Cloudflare', purpose: 'CDN والأمان' },
          { name: 'نسخ احتياطي تلقائي', purpose: 'نسخ احتياطي يومي' }
        ]
      },
      
      // SEO Section
      seoTitle: 'إستراتيجية SEO دولية',
      seoSubtitle: 'تحسين لـ 3 أسواق لغوية',
      
      seoStrategy: 'نهج SEO متعدد اللغات',
      strategyPoints: [
        {
          title: 'بنية دولية',
          items: [
            'علامات hreflang لكل لغة',
            'خرائط موقع XML منفصلة حسب اللغة',
            'عناوين URL أساسية صحيحة',
            'وصوف Meta محلية',
            'محتوى فريد لكل سوق'
          ]
        },
        {
          title: 'الأداء التقني',
          items: [
            'نتيجة Core Web Vitals > 90',
            'وقت التحميل < 1.5ث',
            'صور WebP محسنة',
            'تصغير CSS/JS',
            'كاش HTTP فعال'
          ]
        },
        {
          title: 'المحتوى التقني',
          items: [
            'مصطلحات متخصصة محلية',
            'بنية دلالية HTML5',
            'Rich snippets للمنتجات/الخدمات',
            'مدونة خبرة تقنية',
            'روابط داخلية محسنة'
          ]
        }
      ],
      
      performanceMetrics: 'المؤشرات الرئيسية',
      metrics: [
        { metric: 'Largest Contentful Paint', value: '1.2ث', target: '<2.5ث', status: 'ممتاز' },
        { metric: 'First Input Delay', value: '35ملث', target: '<100ملث', status: 'ممتاز' },
        { metric: 'Cumulative Layout Shift', value: '0.08', target: '<0.1', status: 'ممتاز' },
        { metric: 'Time to Interactive', value: '1.8ث', target: '<3.8ث', status: 'ممتاز' }
      ],
      
      // Results
      resultsTitle: 'التأثير والنتائج',
      resultsSubtitle: 'أداء تجاري وتقني قابل للقياس',
      
      achievements: 'الإنجازات الرئيسية',
      achievementsList: [
        {
          metric: 'التغطية الدولية',
          description: 'موقع متاح بـ 3 لغات لـ 3 أسواق',
          value: '+300%',
          icon: Globe
        },
        {
          metric: 'الخبرة المعروضة',
          description: '8 مجالات تقنية موثقة بالتفصيل',
          value: '100%',
          icon: Building
        },
        {
          metric: 'العملاء المحتملين',
          description: 'طلبات دراسة مؤهلة شهريًا',
          value: '+85%',
          icon: MessageSquare
        },
        {
          metric: 'أداء الموقع',
          description: 'نتيجة Google PageSpeed على الكمبيوتر',
          value: '96/100',
          icon: Zap
        }
      ],
      
      clientTestimonial: {
        quote: 'الموقع المطور قد أظهر خبرتنا التقنية المعقدة بشكل مثالي مع بقائه سهل الوصول لجمهورنا الدولي. النظام متعدد اللغات يعمل بشكل لا تشوبه شائبة وقد ولد فرصًا تجارية ملموسة بالفعل.',
        author: 'الإدارة التقنية',
        position: 'Azure Energy & Water'
      },
      
      keySuccess: 'عوامل النجاح',
      successPoints: [
        'فهم متعمق للتحديات التقنية للقطاع',
        'بنية قابلة للتوسع لمحتوى متعدد اللغات معقد',
        'واجهة توازن بين العمق التقني والبساطة',
        'تحسينات الأداء للجمهور الدولي',
        'دعم مستمر وتحسينات تكرارية'
      ],
      
      projectInfo: {
        status: 'نشط ومحافظ عليه',
        team: 'مطور منفرد',
        stack: 'Laravel, MySQL, Tailwind'
      },
      
      // Video section
      playVideo: 'تشغيل التجربة',
      pauseVideo: 'إيقاف مؤقت',
      videoPlaying: 'جاري التشغيل...'
    }
  };

  const t = content[language];

  const sections = [
    { id: 'overview', label: t.navOverview, icon: Eye },
    { id: 'demo', label: t.navDemo, icon: Video },
    { id: 'features', label: t.navFeatures, icon: Package },
    { id: 'tech', label: t.navTech, icon: Code },
    { id: 'seo', label: t.navSeo, icon: Search },
    { id: 'results', label: t.navResults, icon: TrendingUp }
  ];

  const renderSectionContent = () => {
    switch(activeSection) {
      case 'overview':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.overviewTitle}</h2>
              <p className={`text-base md:text-lg leading-relaxed mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t.overviewIntro}
              </p>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-4 text-blue-500">{t.projectGoals}</h3>
              <div className="space-y-3">
                {t.goalsList.map((goal, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Target size={20} className="text-blue-500 flex-shrink-0 mt-1" />
                    <p className={`text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{goal}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-4 text-blue-500">{t.myRole}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {t.roleDetails.map((detail, index) => (
                  <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="flex items-start gap-3">
                      <CheckCircle size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className={`text-xs md:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'demo':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.demoTitle}</h2>
              <p className={`text-base md:text-lg mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.demoSubtitle}
              </p>
            </div>

            {/* Video Demo */}
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <div className="mb-6">
                <h3 className="text-xl md:text-2xl font-bold mb-2 text-blue-500">{t.videoTitle}</h3>
                <p className={`text-base md:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t.videoDescription}
                </p>
              </div>
              
              {/* Video Player */}
              <div className={`aspect-video rounded-xl overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gray-100'} border-2 ${darkMode ? 'border-gray-700' : 'border-gray-300'} mb-4`}>
                <video
                  ref={videoRef}
                  className="w-full h-full object-contain"
                  controls
                  onPlay={() => setPlayingVideo(true)}
                  onPause={() => setPlayingVideo(false)}
                  poster="/thumbnails/demo-azure.jpg"
                >
                  <source src="/video/demo-azure.mp4" type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture de vidéos.
                </video>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={handleVideoPlay}
                  className={`px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 flex-1 sm:flex-none ${
                    playingVideo
                      ? 'bg-blue-600 text-white'
                      : darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  {playingVideo ? (
                    <>
                      <Pause size={20} />
                      <span className="hidden sm:inline">{t.pauseVideo}</span>
                      <span className="sm:hidden">Pause</span>
                    </>
                  ) : (
                    <>
                      <Play size={20} />
                      <span>{t.playVideo}</span>
                    </>
                  )}
                </button>
                <button
                  onClick={handleVideoPause}
                  className={`px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 flex-1 sm:flex-none ${
                    darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  <Pause size={20} />
                  <span>{t.pauseVideo}</span>
                </button>
              </div>
              
              <div className="mt-6">
                <h4 className="text-lg md:text-xl font-bold mb-4 text-blue-500">{t.keyFeatures}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {t.keyFeaturesList.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className={`text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{feature}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.featuresTitle}</h2>
              <p className={`text-base md:text-lg mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.featuresSubtitle}
              </p>
            </div>

            <div className="space-y-6">
              {t.featuresList.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                    <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                      <div className="p-3 md:p-4 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600 flex-shrink-0">
                        <Icon size={28} className="md:w-8 md:h-8" color="white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl md:text-2xl font-bold mb-3 text-blue-500">{feature.title}</h3>
                        <p className={`text-base md:text-lg mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {feature.description}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {feature.details.map((detail, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                              <p className={`text-sm md:text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{detail}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'tech':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.techTitle}</h2>
              <p className={`text-base md:text-lg mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.techSubtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                <h3 className="text-xl font-bold mb-6 text-blue-500">{t.frontendTech.title}</h3>
                <div className="space-y-4">
                  {t.frontendTech.technologies.map((tech, index) => (
                    <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="font-bold text-blue-500">{tech.name}</p>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{tech.purpose}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                <h3 className="text-xl font-bold mb-6 text-blue-500">{t.backendTech.title}</h3>
                <div className="space-y-4">
                  {t.backendTech.technologies.map((tech, index) => (
                    <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="font-bold text-blue-500">{tech.name}</p>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{tech.purpose}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                <h3 className="text-xl font-bold mb-6 text-blue-500">{t.devOpsTech.title}</h3>
                <div className="space-y-4">
                  {t.devOpsTech.technologies.map((tech, index) => (
                    <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="font-bold text-blue-500">{tech.name}</p>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{tech.purpose}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">Architecture Multilingue</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                  <Languages size={32} className="mx-auto mb-3 text-blue-500" />
                  <h4 className="font-bold mb-2">Base de Données</h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Tables de traduction relationnelles</p>
                </div>
                <div className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                  <Globe size={32} className="mx-auto mb-3 text-blue-500" />
                  <h4 className="font-bold mb-2">Frontend</h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Routes et sélecteur de langue</p>
                </div>
                <div className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                  <Cpu size={32} className="mx-auto mb-3 text-blue-500" />
                  <h4 className="font-bold mb-2">Backend</h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Middleware et gestion du cache</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'seo':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.seoTitle}</h2>
              <p className={`text-base md:text-lg mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.seoSubtitle}
              </p>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">{t.seoStrategy}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                {t.strategyPoints.map((strategy, index) => (
                  <div key={index} className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <h4 className="font-bold text-lg mb-4 text-blue-500">{strategy.title}</h4>
                    <div className="space-y-2">
                      {strategy.items.map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">{t.performanceMetrics}</h3>
              <div className="space-y-4">
                {t.metrics.map((metric, index) => (
                  <div key={index} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-2">
                      <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {metric.metric}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                        metric.status === 'excellent' 
                          ? 'bg-green-500/20 text-green-600'
                          : 'bg-yellow-500/20 text-yellow-600'
                      }`}>
                        {metric.value}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between text-sm gap-1">
                      <span className={darkMode ? 'text-gray-500' : 'text-gray-600'}>
                        Cible: {metric.target}
                      </span>
                      <span className="text-blue-500 font-bold">✓ Atteint</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'results':
        return (
          <div className="space-y-8">
            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-500">{t.resultsTitle}</h2>
              <p className={`text-base md:text-lg mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.resultsSubtitle}
              </p>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">{t.achievements}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {t.achievementsList.map((achievement, index) => {
                  const Icon = achievement.icon;
                  return (
                    <div key={index} className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} text-center`}>
                      <Icon size={32} className="mx-auto mb-3 text-blue-500" />
                      <div className="text-lg md:text-xl font-bold mb-2 text-blue-500">{achievement.metric}</div>
                      <p className={`text-xs md:text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {achievement.description}
                      </p>
                      <div className={`px-3 py-1 rounded-full text-sm font-bold bg-green-500/20 text-green-600`}>
                        {achievement.value}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">Témoignage Client</h3>
              <div className={`p-4 md:p-6 rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-500`}>
                <MessageCircle size={32} className="text-blue-500 mb-4" />
                <blockquote className={`text-lg md:text-xl italic leading-relaxed mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  "{t.clientTestimonial.quote}"
                </blockquote>
                <p className="font-bold text-lg">{t.clientTestimonial.author}</p>
                <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                  {t.clientTestimonial.position}
                </p>
              </div>
            </div>

            <div className={`p-6 md:p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
              <h3 className="text-xl md:text-2xl font-bold mb-6 text-blue-500">{t.keySuccess}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {t.successPoints.map((point, index) => (
                  <div key={index} className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} flex items-start gap-3`}>
                    <CheckCircle size={24} className="text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'} py-4 md:py-8`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        {/* Back Button */}
        <button
          onClick={onBack}
          className={`flex items-center gap-2 mb-6 px-3 py-2 md:px-4 md:py-2 rounded-lg ${
            darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'
          } transition-colors shadow-lg text-sm md:text-base`}
        >
          <ArrowLeft size={18} className="md:w-5 md:h-5" />
          <span>{t.backButton}</span>
        </button>

        {/* Header */}
        <div className="text-center mb-8 md:mb-12 px-2">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 bg-gradient-to-r from-blue-500 to-cyan-600 bg-clip-text text-transparent">
            {t.title}
          </h1>
          <p className={`text-base sm:text-lg md:text-xl ${darkMode ? 'text-gray-400' : 'text-gray-600'} px-2`}>
            {t.subtitle}
          </p>
        </div>

        {/* Hero Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8 md:mb-12">
          {t.stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className={`p-4 md:p-6 rounded-xl text-center ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                <Icon size={20} className="mx-auto mb-2 text-blue-500 md:w-6 md:h-6" />
                <div className="text-lg md:text-2xl font-bold text-blue-500">{stat.value}</div>
                <div className={`text-xs md:text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {stat.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="mb-6 md:mb-8 overflow-x-auto">
          <div className="flex gap-1 md:gap-2 pb-2 min-w-max">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-2 px-3 py-2 md:px-4 md:py-3 rounded-lg font-semibold transition-all whitespace-nowrap text-sm md:text-base ${
                    activeSection === section.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : darkMode
                      ? 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                      : 'bg-white hover:bg-gray-100 text-gray-700 shadow-md'
                  }`}
                >
                  <Icon size={16} className="md:w-5 md:h-5" />
                  <span>{section.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="mb-8 md:mb-12">
          {renderSectionContent()}
        </div>

        {/* Project Info - MODIFIÉ : 3 colonnes au lieu de 4 */}
        <div className={`mt-6 md:mt-8 p-4 md:p-6 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 text-center">
            <div>
              <CheckCircle size={18} className="mx-auto mb-2 text-blue-500 md:w-5 md:h-5" />
              <div className="font-bold text-blue-500 text-sm md:text-base">{t.projectInfo.status}</div>
              <div className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Statut</div>
            </div>
            <div>
              <UsersIcon size={18} className="mx-auto mb-2 text-blue-500 md:w-5 md:h-5" />
              <div className="font-bold text-blue-500 text-sm md:text-base">{t.projectInfo.team}</div>
              <div className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Équipe</div>
            </div>
            <div>
              <Code size={18} className="mx-auto mb-2 text-blue-500 md:w-5 md:h-5" />
              <div className="font-bold text-blue-500 text-sm md:text-base">{t.projectInfo.stack}</div>
              <div className={`text-xs md:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Stack</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}